import React, { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { useTheme } from '@mui/material/styles';
import { GET } from '../../../configs/api';
import { api } from '../../../utils/constants';
import {
  formatInvoicePatternInput,
  invoicePatternErrorMessage,
} from '../../../utils/invoicePattern';

/**
 * Invoice pattern field with random generate (optional letters + required digits, unique per company).
 * Styled for light/dark mode. Used on create and edit client dialogs.
 */
export default function ClientInvoicePatternField({
  value,
  onChange,
  error,
  helperText,
  excludeClientId = null,
  readOnly = false,
  disabled = false,
  textFieldSx,
  onBlur,
  required = false,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState('');

  const actionButtonSx = {
    textTransform: 'none',
    fontSize: '0.8125rem',
    fontWeight: 600,
    borderRadius: 1,
    px: 1.5,
    py: 0.75,
    minWidth: 0,
    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
    color: 'text.primary',
    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
    '&:hover': {
      borderColor: isDarkMode ? '#475569' : '#bdbdbd',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    },
    '&:disabled': {
      borderColor: isDarkMode ? '#334155' : '#e0e0e0',
      color: isDarkMode ? '#64748b' : '#9e9e9e',
    },
  };

  const defaultTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      backgroundColor: 'background.paper',
      '& fieldset': {
        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#475569' : '#bdbdbd',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? '#fff' : '#000',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'text.secondary',
      '&.Mui-focused': {
        color: isDarkMode ? '#fff' : '#000',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      color: 'text.primary',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      letterSpacing: '0.04em',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
    },
  };

  const generateRandomPattern = async () => {
    setSuggestError('');
    setSuggestLoading(true);
    const params = {};
    if (excludeClientId) {
      params.exclude_client_id = excludeClientId;
    }
    const { status, data, message } = await GET(api.suggestClientInvoicePattern, params);
    setSuggestLoading(false);
    if (status === 200 && data?.invoice_pattern) {
      onChange(data.invoice_pattern);
      return;
    }
    setSuggestError(message || data?.message || 'Could not generate invoice pattern.');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        fullWidth
        name="invoice_pattern"
        required={required}
        label="Invoice Pattern"
        placeholder="STA001"
        value={value || ''}
        onBlur={onBlur}
        onChange={(event) => {
          setSuggestError('');
          onChange(formatInvoicePatternInput(event.target.value));
        }}
        error={!!error || !!suggestError}
        helperText={error || suggestError || helperText || invoicePatternErrorMessage}
        slotProps={{
          input: {
            readOnly,
            inputProps: { maxLength: 13, name: 'invoice_pattern' },
          },
        }}
        disabled={disabled}
        sx={textFieldSx || defaultTextFieldSx}
      />
      {!readOnly && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            disabled={disabled || suggestLoading}
            onClick={generateRandomPattern}
            startIcon={
              suggestLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <ShuffleIcon sx={{ fontSize: '1rem !important' }} />
              )
            }
            sx={actionButtonSx}
          >
            Generate random
          </Button>
        </Box>
      )}
    </Box>
  );
}
